import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { ResourceRequestAccessLevel, ResourceRequestStatus } from '~/types';
import { useMeContext } from '~/modules/me';
import { LegacyResourceRequestItem } from '~/modules/projects/resourcing-plan/ResourceRequestChart/components/ResourceRequestItem';
import { checkIfResourceAllocationIsReadOnly } from '~/modules/resourcing/enhancers/withIsResourceAllocationReadonly';
import {
  useRedirectToAssignResource,
  useResourceAllocationToolbarStateContext,
  useResourcePlanFilterBasedOnSearchCriteria
} from '~/modules/resourcing/hooks';
import { useHasPermission } from '~/modules/common/permissions';
import {
  ResourceRequestAllocationActions,
  AssignResourceButton,
  ResourceMatchItem
} from '~/modules/resourcing/common/components';
import { RESOURCE_MATCH_ITEM_BUTTONS } from '~/modules/resourcing/common/components/ResourceMatchItem/enums';
import { useMergeDialogState } from '~/modules/resourcing/common/hooks';
import { updateCacheForCompletedAndMergedRequestsOnResourcingPage } from '~/modules/resourcing/common/hooks/useMarkResourceRequestAsCompleteAndMerge';
import { useSessionStorage, useIsPtiEnabled } from '~/modules/common/hooks';
import useResourceAllocationChartTabStateContext, {
  RESOURCE_ALLOCATION_CHART_TAB_ENUM
} from '~/modules/resourcing/hooks/useResourceAllocationChartTabStateContext';
import { useGetResourceRequestFilter } from '~/modules/resourcing/common/hooks/useGetResourceRequestFilter';
import useProjectResourcingContext from '~/modules/resourcing/contexts/useProjectResourcingContext';
import AllocatedResourceRequestUserItem from './components/AllocatedResourceRequestUserItem';
import useStyles from './useStyles';
import { getCacheUpdateFunctions } from './cacheUpdateFunctions';

const useResourceMatchItemMatchStyles = makeStyles(theme => ({
  matchInfo: {
    padding: theme.spacing(0.5, 0, 0.5, 1),
    margin: theme.spacing(-0.5, 0, -0.5, -1),
    borderRadius: 0
  }
}));

export const AllocatedResourceRequestItem = ({
  classes: { openButton, ...classesOverrides },
  qeIdTag,
  resourceRequest,
  displayEntityType,
  maxSkillLevel,
  requestAccessLevel,
  project,
  displayUnit,
  setAnchorEl,
  setPopoverResourceRequest
}) => {
  const isPtiEnabled = useIsPtiEnabled();
  const history = useHistory();
  const classes = useStyles({ classes: classesOverrides });
  const resourceMatchItemMatchClasses = useResourceMatchItemMatchStyles();
  const {
    featureFlags: {
      isPsaRmpTaskAllocation1Enabled,
      isPsaPraaResourceMatchingEnabled,
      isRmp2025Q3Enhancements
    }
  } = useMeContext();

  const { searchCriteria } = useProjectResourcingContext();
  const { planFilter } = useResourcePlanFilterBasedOnSearchCriteria({
    searchCriteria
  });

  const { scale } = useResourceAllocationToolbarStateContext();
  const chartTabContext = useResourceAllocationChartTabStateContext();
  const filter = useGetResourceRequestFilter();

  const isHiringStatusSubFilterCacheUpdateEnabled =
    isPtiEnabled &&
    chartTabContext?.selectedChartTab ===
      RESOURCE_ALLOCATION_CHART_TAB_ENUM.TOBEHIRED;
  const isNewCacheUpdatesEnabled = isPtiEnabled || isRmp2025Q3Enhancements;

  const { storedValue } = useSessionStorage(
    'HIRING-STATUS-FILTER-QUERY-AND-CACHE-VARIABLES',
    {}
  );
  const { hiringStatusSummaryCountQueryVariables } = storedValue;

  const mergeDialogState = useMergeDialogState();
  const { isResourceAllocationReadOnly } = checkIfResourceAllocationIsReadOnly({
    resourceRequest
  });

  const { resourceAllocations } = resourceRequest;

  const isResourceAllocationExists = resourceAllocations.length !== 0;

  const onAssignResourceClick = useRedirectToAssignResource({
    history,
    resourceRequest,
    scale,
    displayUnit
  });

  const reqClasses = useMemo(() => ({ openButton }), [openButton]);

  const isEditAssignmentEnabled = useHasPermission({
    actionUri:
      'urn:replicon:resource-assignment-action:edit-resource-assignments'
  });

  const { storedValue: resourceRequestsQueryVariables } = useSessionStorage(
    'RESOURCE-REQUESTS-QUERY-VARIABLES',
    null
  );

  const cacheUpdateParams = useMemo(() => {
    return {
      updateMergeCache: updateCacheForCompletedAndMergedRequestsOnResourcingPage,
      resourceRequestsQueryVariables,
      ...(isNewCacheUpdatesEnabled && { planFilter })
    };
  }, [isNewCacheUpdatesEnabled, planFilter, resourceRequestsQueryVariables]);

  const actions = useMemo(
    () => (
      <ResourceRequestAllocationActions
        project={project}
        resourceRequest={resourceRequest}
        requestAccessLevel={requestAccessLevel}
        isProjectManagerView={false}
        isProjectResourcingTab
        isResourceRequestMergeEnabled={isPsaRmpTaskAllocation1Enabled}
        mergeDialogState={mergeDialogState}
        scale={scale}
        displayUnit={displayUnit}
        cacheUpdateParams={cacheUpdateParams}
        getCacheUpdateFunctions={
          isNewCacheUpdatesEnabled &&
          getCacheUpdateFunctions({
            request: resourceRequest,
            hiringStatusSummaryCountQueryVariables,
            resourceRequestsQueryVariables,
            chartTabContext,
            filter,
            summaryCountPlanFilter: planFilter,
            isHiringStatusSubFilterCacheUpdateEnabled,
            isPtiEnabled
          })
        }
      />
    ),
    [
      project,
      resourceRequest,
      requestAccessLevel,
      isPsaRmpTaskAllocation1Enabled,
      mergeDialogState,
      scale,
      displayUnit,
      cacheUpdateParams,
      isPtiEnabled,
      hiringStatusSummaryCountQueryVariables,
      resourceRequestsQueryVariables,
      chartTabContext,
      filter,
      planFilter,
      isHiringStatusSubFilterCacheUpdateEnabled,
      isNewCacheUpdatesEnabled
    ]
  );

  const resourceRequestItemClasses = useMemo(
    () => ({
      clickableColumn: classes.clickableColumn,
      openButton
    }),
    [classes.clickableColumn, openButton]
  );

  return (
    <>
      <div className={classNames(classes.allocationRoot, classes.roleItem)}>
        <LegacyResourceRequestItem
          qeIdTag={qeIdTag}
          actions={actions}
          displayEntityType={displayEntityType}
          resourceRequest={resourceRequest}
          project={project}
          maxSkillLevel={maxSkillLevel}
          classes={resourceRequestItemClasses}
        />
      </div>
      {isPsaPraaResourceMatchingEnabled &&
      resourceRequest.requestStatus === ResourceRequestStatus.Submitted &&
      displayEntityType === 'ROLE' ? (
        <ResourceMatchItem
          matchClasses={resourceMatchItemMatchClasses}
          resourceRequest={resourceRequest}
          buttonType={RESOURCE_MATCH_ITEM_BUTTONS.PROPOSE}
          setAnchorEl={setAnchorEl}
          setPopoverResourceRequest={setPopoverResourceRequest}
          project={project}
        />
      ) : !isResourceAllocationExists ? (
        <div className={classes.allocation}>
          <AssignResourceButton
            qeIdTag={qeIdTag}
            quantity={resourceRequest.quantity}
            onClick={onAssignResourceClick}
          />
        </div>
      ) : (
        <div className={classes.resourceItems}>
          {resourceAllocations.map((resourceAllocation, index) => {
            const resourceUri = resourceAllocation.user
              ? resourceAllocation.user.userUri
              : `NoUser`;

            return (
              <div key={uuidv4()} className={classes.userItem}>
                <AllocatedResourceRequestUserItem
                  classes={reqClasses}
                  qeIdTag={`${qeIdTag}_${resourceUri}`}
                  isReadonly={
                    isResourceAllocationReadOnly || !isEditAssignmentEnabled
                  }
                  resourceRequest={resourceRequest}
                  resourceAllocation={resourceAllocation}
                  resourceAllocationUserUri={resourceAllocation.user.userUri}
                  resourceAllocationLoad={resourceAllocation.load}
                  requestAccessLevel={requestAccessLevel}
                  isAdjustedLoading={resourceAllocation.isAdjustedLoading}
                  project={project}
                />
              </div>
            );
          })}
          {!isResourceAllocationReadOnly &&
            resourceAllocations.length < resourceRequest.quantity && (
              <div className={classes.allocationAndResource}>
                <AssignResourceButton
                  qeIdTag={qeIdTag}
                  quantity={
                    resourceRequest.quantity - resourceAllocations.length
                  }
                  onClick={onAssignResourceClick}
                />
              </div>
            )}
        </div>
      )}
    </>
  );
};

AllocatedResourceRequestItem.propTypes = {
  qeIdTag: PropTypes.string,
  classes: PropTypes.object,
  resourceRequest: PropTypes.object.isRequired,
  displayEntityType: PropTypes.oneOf(['PROJECT', 'ROLE']),
  requestAccessLevel: PropTypes.oneOf([
    ResourceRequestAccessLevel.All,
    ResourceRequestAccessLevel.ResourceManager
  ]),
  project: PropTypes.object,
  maxSkillLevel: PropTypes.number,
  displayUnit: PropTypes.string,
  setAnchorEl: PropTypes.func.isRequired,
  setPopoverResourceRequest: PropTypes.func.isRequired
};

export default AllocatedResourceRequestItem;
